import * as React from "react"
import { addDays, format, parse, isValid, startOfDay, endOfDay, subDays } from "date-fns"
import { CalendarIcon } from "lucide-react"
import * as chrono from "chrono-node"
import { type DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DateTimeRange {
  fromDate: string
  fromTime: string
  toDate: string
  toTime: string
}

interface DateTimeRangePickerProps {
  value: DateTimeRange
  onChange: (value: DateTimeRange) => void
  className?: string
}

const PRESETS = [
  { label: "Today", getValue: () => ({ from: startOfDay(new Date()), to: new Date() }) },
  { label: "Yesterday", getValue: () => ({ from: startOfDay(subDays(new Date(), 1)), to: endOfDay(subDays(new Date(), 1)) }) },
  { label: "Last 7 days", getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: "Last 30 days", getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: "Last 90 days", getValue: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
] as const

function formatDateStr(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function formatTimeStr(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0")
  const m = String(date.getMinutes()).padStart(2, "0")
  return `${h}:${m}`
}

function parseDate(dateStr: string): Date | undefined {
  if (!dateStr) return undefined
  const d = parse(dateStr, "yyyy-MM-dd", new Date())
  return isValid(d) ? d : undefined
}

function DateTimeRangePicker({ value, onChange, className }: DateTimeRangePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [nlInput, setNlInput] = React.useState("")

  const fromDate = parseDate(value.fromDate)
  const toDate = parseDate(value.toDate)

  const calendarRange: DateRange | undefined =
    fromDate || toDate ? { from: fromDate, to: toDate } : undefined

  const applyRange = (from: Date, to: Date) => {
    onChange({
      fromDate: formatDateStr(from),
      fromTime: formatTimeStr(from),
      toDate: formatDateStr(to),
      toTime: formatTimeStr(to),
    })
  }

  const handleCalendarSelect = (range: DateRange | undefined) => {
    if (!range) return
    onChange({
      fromDate: range.from ? formatDateStr(range.from) : value.fromDate,
      fromTime: value.fromTime,
      toDate: range.to ? formatDateStr(range.to) : value.toDate,
      toTime: value.toTime,
    })
  }

  const handlePreset = (preset: typeof PRESETS[number]) => {
    const { from, to } = preset.getValue()
    applyRange(from, to)
    setNlInput("")
  }

  const handleNlInput = (text: string) => {
    setNlInput(text)
    const results = chrono.parse(text, new Date(), { forwardDate: false })
    if (results.length > 0) {
      const result = results[0]
      const from = result.start.date()
      const to = result.end ? result.end.date() : from
      applyRange(from, to)
    }
  }

  const displayValue = React.useMemo(() => {
    const parts: string[] = []
    if (fromDate) {
      let s = format(fromDate, "MMM dd, yyyy")
      if (value.fromTime) s += ` ${value.fromTime}`
      parts.push(s)
    }
    if (toDate) {
      let s = format(toDate, "MMM dd, yyyy")
      if (value.toTime) s += ` ${value.toTime}`
      parts.push(s)
    }
    return parts.join(" – ")
  }, [fromDate, toDate, value.fromTime, value.toTime])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal h-9",
            !fromDate && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="size-4 text-muted-foreground" />
          {displayValue || <span>Pick a date range</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col gap-3 p-3 pb-0">
          <Input
            placeholder='Try "last 7 days" or "March 1 to 15"'
            value={nlInput}
            onChange={(e) => handleNlInput(e.target.value)}
            className="h-8 text-sm"
          />
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
        <Calendar
          mode="range"
          defaultMonth={fromDate}
          selected={calendarRange}
          onSelect={handleCalendarSelect}
          numberOfMonths={2}
        />
        <div className="flex items-center gap-4 border-t px-3 py-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Start time</Label>
            <Input
              type="time"
              value={value.fromTime}
              onChange={(e) => onChange({ ...value, fromTime: e.target.value })}
              className="h-8 w-[110px] text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">End time</Label>
            <Input
              type="time"
              value={value.toTime}
              onChange={(e) => onChange({ ...value, toTime: e.target.value })}
              className="h-8 w-[110px] text-sm"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DateTimeRangePicker }
export type { DateTimeRange }
